import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";
import {
  buildCoachingRecordSummaryFromCount,
  buildLeadershipRecordSummaryFromCount,
  type ContinuityRecordKind,
  type ContinuityRecordSummary,
} from "@/lib/behavior/continuityRecord";

type ContinuityMomentCountClient = {
  from: (table: "continuity_moments") => {
    select: (
      columns: string,
      options: { count: "exact"; head: true },
    ) => ContinuityMomentCountQuery;
  };
};

type ContinuityMomentCountQuery = {
  eq: (column: string, value: string) => ContinuityMomentCountQuery;
  then: (
    onfulfilled: (value: { count: number | null; error: Error | null }) => unknown,
    onrejected?: (reason: unknown) => unknown,
  ) => Promise<unknown>;
};

function isMissingContinuityMomentsError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const candidate = error as { code?: string };
  return candidate.code === "42P01" || candidate.code === "PGRST205";
}

async function countContinuityMoments(params: {
  kind: ContinuityRecordKind;
  leaderId?: string | null;
  coachId?: string | null;
  relationshipLeaderId?: string | null;
}): Promise<number> {
  let response: { count: number | null; error: Error | null };
  try {
    const client = supabase as unknown as ContinuityMomentCountClient;
    let query = client
      .from("continuity_moments")
      .select("id", { count: "exact", head: true })
      .eq("kind", params.kind);

    if (params.kind === "leadership" && params.leaderId) {
      query = query.eq("leader_id", params.leaderId);
    }
    if (params.kind === "coaching" && params.coachId) {
      query = query.eq("coach_id", params.coachId);
    }
    if (params.kind === "coaching" && params.relationshipLeaderId) {
      query = query.eq("relationship_leader_id", params.relationshipLeaderId);
    }

    response = await query as { count: number | null; error: Error | null };
  } catch {
    console.error("[continuity-record-summary] continuity_record_summary_query_failed");
    throw new Error("Record updates could not be loaded. Please try again.");
  }
  if (response.error) {
    console.error("[continuity-record-summary] continuity_record_summary_query_failed");
    if (isMissingContinuityMomentsError(response.error)) return 0;
    throw new Error("Record updates could not be loaded. Please try again.");
  }
  return response.count ?? 0;
}

export function useLeadershipRecordSummary(leaderId?: string | null) {
  return useQuery<ContinuityRecordSummary>({
    queryKey: ["continuity-record-summary", "leadership", leaderId],
    enabled: Boolean(leaderId),
    staleTime: 60_000,
    queryFn: async () => {
      const count = await countContinuityMoments({ kind: "leadership", leaderId });
      return buildLeadershipRecordSummaryFromCount(count);
    },
  });
}

export function useCoachingRecordSummary(params: {
  coachId?: string | null;
  relationshipLeaderId?: string | null;
  relationshipLeaderName?: string | null;
  enabled?: boolean;
}) {
  return useQuery<ContinuityRecordSummary>({
    queryKey: [
      "continuity-record-summary",
      "coaching",
      params.coachId,
      params.relationshipLeaderId ?? "portfolio",
      params.relationshipLeaderName ?? null,
    ],
    enabled: Boolean(params.enabled ?? true) && Boolean(params.coachId),
    staleTime: 60_000,
    queryFn: async () => {
      const count = await countContinuityMoments({
        kind: "coaching",
        coachId: params.coachId,
        relationshipLeaderId: params.relationshipLeaderId,
      });
      return buildCoachingRecordSummaryFromCount(count, params.relationshipLeaderName);
    },
  });
}
